<template>
  <div v-if="isReportMode">
    <ReportItemContainer>
      <template #question>
        <div v-if="question?.text">
          {{ question.text }}
        </div>
      </template>
      <template #answer>
        <p v-for="displayAnswer in displayAnswers" :key="displayAnswer">
          {{ displayAnswer }}
        </p>
      </template>
    </ReportItemContainer>
  </div>
  <div v-else>
    <p v-if="question?.text" class="mb-4">
      {{ question.text }}
      <InfoPopup
        v-if="shouldDisplayInfoIcon"
        :title="question.helpTitle"
        :content="question.helpText"
      />
    </p>
    <PAutocompleteMulti
      :select-items="selectedValues"
      :items="items"
      :disabled="isReadOnlyMode"
      @field-updated="updateItems"
      :chips="true"
    />
  </div>
</template>

<script setup lang="ts">
import type { QuestionModelDto } from "@/models/dto/questionModelDto";
import { computed } from "vue";
import InfoPopup from "@/components/custom/InfoPopup.vue";
import { ref } from "vue";
import ReportItemContainer from "@/components/containers/ReportItemContainer.vue";
import { countriesWithChineseRegionsAsItems } from "@/modules/countries";
import { SelectType } from "@/models/enum/selectType";
import type { SelectItem } from "@/models/selectItem";
import PAutocompleteMulti from "@/components/custom/PAutocompleteMulti.vue";

const props = withDefaults(
  defineProps<{
    questionModel: QuestionModelDto;
    answer?: string | object | null;
    isReportMode?: boolean;
    isReadOnlyMode?: boolean;
  }>(),
  {
    isReportMode: false,
    isReadOnlyMode: false,
  }
);

const emit = defineEmits<{
  (e: "qEvent", newValue: string[]): void;
}>();

const question = computed(() => props.questionModel.question);

const items = computed(() => {
  switch (question.value.dataSource) {
    case SelectType.CountriesWithChineseRegions: {
      return countriesWithChineseRegionsAsItems;
    }
    default: {
      return new Array<SelectItem>();
    }
  }
});

const selectedValues = ref((props.answer as string[]) ?? []);
const displayAnswers = ref(getDisplayTextFromItems(selectedValues.value));

const shouldDisplayInfoIcon = computed(
  () => question.value.helpText && question.value.helpText?.length > 0
);

function updateItems(newValues: string[]): void {
  selectedValues.value = newValues;
  emit("qEvent", selectedValues.value);
}

function getDisplayTextFromItems(values: string[]): string[] {
  const _names = new Array<string>();
  values.forEach((val: string) => {
    const idx = items.value.findIndex(
      (item: SelectItem) =>
        item.id.toLocaleLowerCase() === val.toLocaleLowerCase()
    );
    if (idx !== -1) {
      _names.push(items.value[idx].text);
    }
  });
  return _names;
}
</script>
